import { useEffect, useState } from "react";
import { AlertTriangle, Eye, EyeOff } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { makeRequest } from "@/utils/makeRequest";
import { toast } from "sonner";
import { useAppSelector } from "@/store/hooks";
import { getErrorMessage } from "@/utils/getErrorMessage";

import { queryClient } from "@/services/queryClient";

import { Spinner } from "../ui/spinner";
import { OtpInput } from "../shared/OtpInput";

const OTP_LENGTH = 6;
const OTP_COOLDOWN_SECONDS = 60;

interface AdminVerificationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (payload: { otp: string; securityCode: string }) => Promise<void>;
  systemType?: string;
  isSwap?: boolean;
}

export function AdminVerificationDialog({
  open,
  onOpenChange,
  onSuccess,
  systemType,
  isSwap,
}: AdminVerificationDialogProps) {
  const [securityCode, setSecurityCode] = useState("");

  const [loading, setLoading] = useState(false);

  const [showSecurityCode, setShowSecurityCode] = useState(false);

  const auth = useAppSelector((state) => state.auth);

  const user = auth?.user;

  useEffect(() => {
    if (!open) {
      const timeout = setTimeout(() => {
        setSecurityCode("");
        setShowSecurityCode(false);
      }, 200);

      return () => clearTimeout(timeout);
    }
  }, [open]);

  const handleRequestOtp = async () => {
    if (!user?.email) {
      throw new Error("Admin email address is unavailable.");
    }

    const res = await makeRequest.put("/auth/email/verify/otp/two-fa", {
      email: user.email,
    });

    const data = res?.data;

    console.log("ADMIN VERIFICATION OTP DATA", data);

    return {
      resendCooldownSeconds:
        data?.otp?.resendCooldownSeconds ?? OTP_COOLDOWN_SECONDS,

      message: data?.message || "OTP sent successfully.",
    };
  };

  const handleVerify = async (otp: string) => {
    if (!securityCode) {
      toast.error("Please enter your security code.");

      return;
    }

    if (!isSwap && !otp) {
      toast.error("Please enter your verification code.");

      return;
    }

    try {
      setLoading(true);

      await onSuccess({
        otp,
        securityCode,
      });

      setSecurityCode("");
      setShowSecurityCode(false);

      onOpenChange(false);

      queryClient.invalidateQueries({
        queryKey: ["transactions:all"],
      });

      queryClient.invalidateQueries({
        queryKey: ["wallet:details", systemType],
        refetchType: "all",
      });

      queryClient.invalidateQueries({
        queryKey: ["logs"],
      });
    } catch (error) {
      /**
       * Throw the error back to OtpInput so that
       * its verification lifecycle remains correct.
       */
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleSwapVerify = async () => {
    if (!securityCode || loading) {
      return;
    }

    try {
      setLoading(true);

      await onSuccess({
        otp: "",
        securityCode,
      });

      setSecurityCode("");
      setShowSecurityCode(false);

      onOpenChange(false);

      queryClient.invalidateQueries({
        queryKey: ["transactions:all"],
      });

      queryClient.invalidateQueries({
        queryKey: ["wallet:details", systemType],
        refetchType: "all",
      });

      queryClient.invalidateQueries({
        queryKey: ["logs"],
      });
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="flex items-center justify-center flex-col mb-5">
          <AlertTriangle className="w-10 h-10" />

          <DialogTitle className="text-yellow-500 text-center">
            Transaction Verification
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {!isSwap && (
            <div className="space-y-2">
              <Label className="text-center w-full flex items-center justify-center mb-2">
                One-Time Password (OTP)
              </Label>

              <OtpInput
                length={OTP_LENGTH}
                cooldown={OTP_COOLDOWN_SECONDS}
                onRequestOtp={handleRequestOtp}
                onVerifyOtp={handleVerify}
                requestLabel="Request OTP"
                resendLabel="Resend OTP"
                verifyLabel="Verify"
                verifyDisabled={!securityCode || loading}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label>Security Code</Label>

            <div className="relative">
              <Input
                disabled={loading}
                className="h-11 pr-10"
                type={showSecurityCode ? "text" : "password"}
                value={securityCode}
                onChange={(e) => setSecurityCode(e.target.value)}
                placeholder="Enter security code"
              />

              <button
                type="button"
                onClick={() => setShowSecurityCode((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                aria-label={
                  showSecurityCode ? "Hide security code" : "Show security code"
                }
              >
                {showSecurityCode ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          {isSwap && (
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Cancel
              </Button>

              <Button
                className="flex-1"
                onClick={handleSwapVerify}
                disabled={loading || !securityCode}
              >
                {loading ? (
                  <div className="flex items-center justify-center gap-2">
                    <Spinner />
                    Processing...
                  </div>
                ) : (
                  "Verify"
                )}
              </Button>
            </div>
          )}

          {!isSwap && (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
